import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { openRazorpayCheckout } from "../lib/razorpay";
import { toast } from "sonner";

const EXPLORER_PLANS = [
  {
    id: "free",
    label: "Explorer Free",
    price: "₹0",
    tagline: "Free forever",
    features: ["Browse mentors", "Book classes", "Pay in-app", "First 2 reviews"],
  },
  {
    id: "plus_monthly",
    label: "Explorer Plus",
    price: "₹299/mo",
    tagline: "Most popular",
    features: ["All reviews", "Priority support", "Early access", "Family calendar", "Class reminders"],
  },
  {
    id: "plus_yearly",
    label: "Explorer Plus (Yearly)",
    price: "₹2999/yr",
    tagline: "Save ₹589 vs monthly",
    features: ["Everything in Plus", "16% off yearly", "Locked-in price"],
  },
];

export default function Signup() {
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "teacher" ? "teacher" : "parent";
  const initialPlan = ["plus_monthly", "plus_yearly"].includes(params.get("plan")) ? params.get("plan") : "free";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(initialRole);
  const [selectedPlan, setSelectedPlan] = useState(initialPlan);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      toast.error("Please accept the Terms & Conditions and Privacy Policy to continue.");
      return;
    }
    setSubmitting(true);
    const res = await register({ name, email, password, role, accepted_terms: true });
    if (!res.ok) {
      setSubmitting(false);
      return toast.error(res.error || "Sign up failed");
    }
    toast.success("Account created!");

    // Explorer + Plus plan chosen: trigger Razorpay checkout right after registration.
    if (role === "parent" && selectedPlan !== "free") {
      try {
        const { data: order } = await api.post("/subscription/create-order", { plan_id: selectedPlan });
        await openRazorpayCheckout({
          keyId: order.key_id,
          amount: order.amount,
          currency: order.currency,
          name: "Rate-ED",
          description: order.plan_name,
          orderId: order.order_id,
          prefill: { email, name },
          onSuccess: async (rzp) => {
            await api.post("/subscription/verify", rzp);
            toast.success("Explorer Plus activated!");
            navigate("/parent/dashboard");
          },
          onDismiss: () => {
            toast.info("You can upgrade to Explorer Plus anytime from your dashboard.");
            navigate("/parent/dashboard");
          },
        });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not start Plus checkout. Try again from your dashboard.");
        navigate("/parent/dashboard");
      }
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    if (res.user.role === "teacher") navigate("/teacher/dashboard");
    else navigate("/parent/dashboard");
  };

  const RoleCard = ({ value, title, desc }) => (
    <button
      type="button"
      onClick={() => setRole(value)}
      data-testid={`signup-role-${value}-btn`}
      className={`flex-1 rounded-2xl p-4 text-left border-2 transition-all duration-200 ${
        role === value ? "border-[#13A699] bg-[#FFF7ED]" : "border-black/10 bg-white hover:border-[#13A699]/40"
      }`}
    >
      <div className="font-display font-bold text-lg" style={{ color: "#13A699" }}>{title}</div>
      <div className="text-xs mt-1" style={{ color: "#13A699" }}>{desc}</div>
    </button>
  );

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12" data-testid="signup-page">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md bg-white rounded-3xl p-8 shadow-sm border border-black/5"
        data-testid="signup-form"
      >
        <h1 className="font-display text-3xl font-bold uppercase" style={{ color: "#13A699" }}>Create Account</h1>
        <p className="mt-1 text-sm" style={{ color: "#13A699" }}>Join Rate-ED in less than a minute.</p>

        <div className="mt-5 flex gap-3">
          <RoleCard value="parent" title="Explorer" desc="Find mentor for your skill" />
          <RoleCard value="teacher" title="Mentor" desc="Onboard & accept bookings" />
        </div>

        <label className="block mt-5 text-sm font-semibold" style={{ color: "#13A699" }}>Name</label>
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 w-full rounded-xl border border-black/10 px-4 py-3 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
          data-testid="signup-name-input"
        />
        <label className="block mt-3 text-sm font-semibold" style={{ color: "#13A699" }}>Email</label>
        <input
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 w-full rounded-xl border border-black/10 px-4 py-3 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
          data-testid="signup-email-input"
        />
        <label className="block mt-3 text-sm font-semibold" style={{ color: "#13A699" }}>Password</label>
        <input
          required
          type="password"
          minLength={6}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1 w-full rounded-xl border border-black/10 px-4 py-3 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
          data-testid="signup-password-input"
        />

        {/* Explorer plan selector (only for parent role) */}
        {role === "parent" && (
          <div className="mt-5" data-testid="signup-plan-selector">
            <label className="block text-sm font-semibold mb-2" style={{ color: "#13A699" }}>
              Choose your Explorer plan
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {EXPLORER_PLANS.map((p) => {
                const active = selectedPlan === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelectedPlan(p.id)}
                    className="text-left rounded-2xl border-2 p-3 transition-all"
                    style={{
                      borderColor: active ? "#13A699" : "#13A69922",
                      background: active ? "#FFF7ED" : "#fff",
                    }}
                    data-testid={`signup-plan-${p.id}`}
                  >
                    <div className="flex items-baseline justify-between">
                      <span className="font-bold text-sm" style={{ color: "#13A699" }}>{p.label}</span>
                      <span className="font-bold text-sm" style={{ color: "#F97316" }}>{p.price}</span>
                    </div>
                    <p className="text-[10px] mt-0.5" style={{ color: "#13A69988" }}>{p.tagline}</p>
                    <ul className="mt-2 space-y-0.5">
                      {p.features.map((f) => (
                        <li key={f} className="text-[11px]" style={{ color: "#13A699BB" }}>• {f}</li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-[10px]" style={{ color: "#13A69988" }}>
              You can upgrade or downgrade anytime from your dashboard. Razorpay checkout will open after account creation for paid plans.
            </p>
          </div>
        )}

        {/* Mandatory T&C + Privacy Policy consent (server also enforces accepted_terms=true). */}
        <label
          className="mt-5 flex items-start gap-3 text-sm cursor-pointer select-none"
          style={{ color: "#13A699" }}
          data-testid="signup-terms-label"
        >
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-2 border-[#13A699] accent-[#13A699] cursor-pointer"
            data-testid="signup-terms-checkbox"
            required
          />
          <span>
            I have read and agree to the{" "}
            <Link
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold underline"
              data-testid="signup-terms-link"
            >
              Terms &amp; Conditions
            </Link>{" "}
            and{" "}
            <Link
              to="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold underline"
              data-testid="signup-privacy-link"
            >
              Privacy Policy
            </Link>
            .
          </span>
        </label>

        <button
          type="submit"
          disabled={submitting || !acceptedTerms}
          className="btn-yellow w-full mt-6 py-3 text-base disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="signup-submit-btn"
        >
          {submitting ? "Creating..." : "Sign Up"}
        </button>

        <div className="mt-4 text-sm text-center" style={{ color: "#13A699" }}>
          Already have an account?{" "}
          <Link to="/login" className="font-bold" data-testid="signup-login-link">Login</Link>
        </div>
      </form>
    </div>
  );
}
